import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { submitGuideVote, getGuideSectionVotes } from '@/lib/api';

// Persistent per-browser anonymous voter id
function getVoterSessionId() {
  try {
    let id = localStorage.getItem('surplify_voter_id');
    if (!id) {
      id = `v_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem('surplify_voter_id', id);
    }
    return id;
  } catch {
    return `v_${Date.now()}`;
  }
}

export function GuideSectionVote({ sectionId }) {
  const { t } = useTranslation();
  const [myVote, setMyVote] = useState(null);
  const [up, setUp] = useState(0);
  const [down, setDown] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const sessionId = getVoterSessionId();

  useEffect(() => {
    let cancelled = false;
    getGuideSectionVotes(sectionId, sessionId)
      .then(({ data }) => {
        if (cancelled) return;
        setMyVote(data.my_vote || null);
        setUp(data.up || 0);
        setDown(data.down || 0);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [sectionId, sessionId]);

  const handleVote = async (vote) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await submitGuideVote({ sectionId, vote, voterSessionId: sessionId });
      setMyVote(data.my_vote);
      setUp(data.up);
      setDown(data.down);
      toast.success(vote === 'up' ? t('guide.vote.thanksUp') : t('guide.vote.thanksDown'));
    } catch (e) {
      toast.error(t('guide.vote.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 flex items-center gap-3 text-xs"
      data-testid={`guide-vote-${sectionId}`}
    >
      <span className="text-muted-foreground font-medium">{t('guide.vote.helpful')}</span>
      <button
        type="button"
        onClick={() => handleVote('up')}
        disabled={submitting}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
          myVote === 'up'
            ? 'bg-emerald-100 border-emerald-300 text-emerald-800 dark:bg-emerald-900/50 dark:border-emerald-700 dark:text-emerald-100'
            : 'border-slate-200 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800'
        }`}
        data-testid={`guide-vote-up-${sectionId}`}
        aria-label={t('guide.vote.up')}
      >
        <ThumbsUp className="w-3 h-3" /> {up}
      </button>
      <button
        type="button"
        onClick={() => handleVote('down')}
        disabled={submitting}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border transition-colors ${
          myVote === 'down'
            ? 'bg-rose-100 border-rose-300 text-rose-800 dark:bg-rose-900/50 dark:border-rose-700 dark:text-rose-100'
            : 'border-slate-200 hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800'
        }`}
        data-testid={`guide-vote-down-${sectionId}`}
        aria-label={t('guide.vote.down')}
      >
        <ThumbsDown className="w-3 h-3" /> {down}
      </button>
    </div>
  );
}
